import React, { useCallback, useEffect, useState } from 'react';
import { researchosService } from '../../services/researchosService';

// Quorum (non-admin) view of Backfill & Runs: the REQUEST workflow. Team members submit a
// channel + window + min-duration; a superuser gets an email and approves/rejects; the
// approved request links its Run so the requester can track fetch progress here. No direct
// spend from this panel — the proxy/extraction cost decision stays with the superuser.
const GOLD = 'rgb(var(--brand-gold-rgb))';

const STATUS_PILL = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

export default function BackfillRequestPanel() {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [years, setYears] = useState('1');
  const [minDur, setMinDur] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [reqs, setReqs] = useState([]);

  const load = useCallback(async () => {
    try { setReqs((await researchosService.getBackfillRequests()).data || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  // poll while any approved request's run is still fetching
  useEffect(() => {
    const active = reqs.some((r) => r.status === 'pending'
      || (r.run && (r.run.status === 'running' || r.run.status === 'queued')));
    if (!active) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [reqs, load]);

  const submit = async () => {
    if (!url.trim()) { setMsg('Channel URL required.'); return; }
    setBusy(true); setMsg('');
    try {
      await researchosService.submitBackfillRequest({
        channel_url: url.trim(), name: name.trim() || undefined, years,
        min_duration: minDur ? parseInt(minDur, 10) : undefined, note: note.trim() || undefined,
      });
      setMsg('Request submitted — an admin has been notified and will approve or reject it.');
      setUrl(''); setName(''); setNote(''); setMinDur('');
      load();
    } catch (e) {
      setMsg('Failed: ' + (e?.response?.data?.error || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="mb-5 rounded-lg border border-gray-200 p-4 dark:border-gray-800">
        <div className="mb-1 text-[11px] uppercase tracking-wider text-gray-500">Request a channel backfill</div>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Submit a YouTube channel you want ingested. An admin reviews every request; once approved,
          the backfill runs and you can track its progress below.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[240px] flex-1">
            <label className="text-xs text-gray-500">Channel URL</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.youtube.com/@channel"
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700" />
          </div>
          <div className="w-40">
            <label className="text-xs text-gray-500">Display name (optional)</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700" />
          </div>
          <div className="w-24">
            <label className="text-xs text-gray-500">Window</label>
            <select value={years} onChange={(e) => setYears(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900">
              <option value="1">1 year</option>
              <option value="2">2 years</option>
              <option value="3">3 years</option>
              <option value="all">All time</option>
            </select>
          </div>
          <div className="w-28">
            <label className="text-xs text-gray-500">Min duration (min)</label>
            <input type="number" min="0" value={minDur} onChange={(e) => setMinDur(e.target.value)} placeholder="e.g. 10"
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700" />
          </div>
          <button onClick={submit} disabled={busy}
            className="rounded px-4 py-1.5 text-sm font-medium disabled:opacity-50"
            style={{ color: GOLD, border: `1px solid ${GOLD}` }}>
            {busy ? 'Submitting…' : 'Request'}
          </button>
        </div>
        <div className="mt-2">
          <label className="text-xs text-gray-500">Why this channel? (optional)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. strong semis coverage, weekly earnings recaps"
            className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700" />
        </div>
        {msg && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{msg}</p>}
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-800">
        <div className="border-b border-gray-100 px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 dark:border-gray-800/60">
          My requests
        </div>
        {reqs.length === 0 ? (
          <p className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">No requests yet.</p>
        ) : reqs.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0 dark:border-gray-800/60">
            <span className={`rounded px-1.5 py-0.5 text-[10px] ${STATUS_PILL[r.status] || ''}`}>{r.status}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{r.name || r.channel_url}</span>
            <span className="text-gray-500">win {r.years === 'all' ? 'all' : `${r.years}y`}</span>
            {r.min_duration != null && <span className="text-gray-500">≥{r.min_duration}m</span>}
            {r.status === 'approved' && r.run && (
              <span className="tabular-nums text-gray-600 dark:text-gray-300">
                run: {r.run.status} · <span className="text-green-600 dark:text-green-400">{r.run.new ?? 0} fetched</span>
                {r.run.enumerated ? ` of ${r.run.enumerated}` : ''}
                {(r.run.failed ?? 0) > 0 && <span className="text-red-500"> · {r.run.failed} failed</span>}
              </span>
            )}
            {r.status === 'rejected' && r.reject_reason && (
              <span className="text-xs text-red-500">“{r.reject_reason}”</span>
            )}
            <span className="ml-auto text-xs text-gray-400">{new Date(r.created_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
